import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import type { Player } from "@mdj/shared";
import { api, MOCK } from "../lib/api";
import { Recorder } from "../lib/recorder";
import { Button } from "./Glass";

type Phase = "idle" | "recording" | "saving" | "saved" | "removing" | "error";

// Record the player's name announcement. Recording SAVES on stop — it is not
// tied to assigning a clip — so a coach can (re)record any player any time.
export function Announcer({ player, onSaved }: { player: Player; onSaved: (p: Player) => void }) {
  const [phase, setPhase] = useState<Phase>(player.announcementKey ? "saved" : "idle");
  const [elapsed, setElapsed] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const rec = useRef(new Recorder());
  const startedAt = useRef(0);
  const audio = useRef<HTMLAudioElement | null>(null);
  const first = player.name.split(" ")[0];

  useEffect(() => { setPhase(player.announcementKey ? "saved" : "idle"); setErr(null); }, [player.id, player.announcementKey]);
  useEffect(() => () => rec.current.cancel(), []);

  // Live timer while recording.
  useEffect(() => {
    if (phase !== "recording") return;
    const t = setInterval(() => setElapsed((Date.now() - startedAt.current) / 1000), 100);
    return () => clearInterval(t);
  }, [phase]);

  const start = async () => {
    setErr(null);
    try {
      await rec.current.start();
      startedAt.current = Date.now(); setElapsed(0); setPhase("recording");
    } catch (e) {
      // Mock/dev only: no microphone in the render rig — simulate a take so the
      // states can be seen. Never in production.
      if (MOCK()) { startedAt.current = Date.now(); setElapsed(0); setPhase("recording"); mockTake.current = Date.now(); return; }
      setErr(e instanceof DOMException && e.name === "NotAllowedError" ? "Microphone permission was denied — allow it in your browser settings." : "Microphone not available on this device.");
      setPhase("error");
    }
  };
  const mockTake = useRef(0);

  const stop = async () => {
    setPhase("saving");
    try {
      let blob: Blob;
      if (mockTake.current) { blob = new Blob([new Uint8Array(1200)], { type: "audio/webm" }); mockTake.current = 0; }
      else blob = await rec.current.stop();
      if (blob.size < 200) throw new Error("That was too short — hold the button and say the name.");
      const p = await api.uploadAnnouncement(player.id, blob);
      onSaved(p); setPhase("saved");
    } catch (e) { setErr((e as Error).message); setPhase("error"); }
  };

  const remove = async () => {
    if (!confirm(`Remove ${first}'s announcement? Game day will play just the song.`)) return;
    audio.current?.pause(); setPlaying(false);
    setPhase("removing"); setErr(null);
    try { const p = await api.deleteAnnouncement(player.id); onSaved(p); setPhase("idle"); }
    catch (e) { setErr((e as Error).message); setPhase("error"); }
  };

  const play = async () => {
    if (audio.current && !audio.current.paused) { audio.current.pause(); audio.current.currentTime = 0; setPlaying(false); return; }
    try {
      const url = await api.announcementUrl(player.id);
      const a = new Audio(url); audio.current = a;
      a.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
      await a.play(); setPlaying(true);
    } catch { setErr("Couldn't play that back."); }
  };

  const clock = `${Math.floor(elapsed / 60)}:${String(Math.floor(elapsed % 60)).padStart(2, "0")}`;

  return (
    <motion.section className="glass glass-strong announcer" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} aria-live="polite">
      <div className="ann-head">
        <div>
          <div className="ann-title">Announce {first}</div>
          <div className="hint">
            {phase === "idle" && `Tap record and say “Now batting, number ${player.number ?? "…"}, ${player.name}!”`}
            {phase === "recording" && "Recording — tap stop when you're done"}
            {phase === "saving" && "Saving…"}
            {phase === "removing" && "Removing…"}
            {phase === "saved" && "Announcement saved — plays before the song"}
            {phase === "error" && (err ?? "Something went wrong")}
          </div>
        </div>
        <AnimatePresence>
          {phase === "recording" && (
            <motion.div key="timer" className="ann-timer" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
              <motion.span className="dot" animate={{ opacity: [1, 0.25, 1] }} transition={{ duration: 1, repeat: Infinity }} />
              {clock}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="ann-controls">
        {phase === "recording" ? (
          <Button primary block onClick={stop} aria-label="Stop recording" style={{ background: "linear-gradient(135deg,#ff8a8a,#ff5d5d)", color: "#3a0000" }}>■ Stop</Button>
        ) : phase === "saving" || phase === "removing" ? (
          <Button block disabled>{phase === "saving" ? "Saving…" : "Removing…"}</Button>
        ) : phase === "saved" ? (
          <>
            <Button onClick={play} aria-label={playing ? "Stop playback" : "Play announcement"}>{playing ? "■ Stop" : "▶ Play"}</Button>
            <Button ghost onClick={start} aria-label="Re-record announcement">● Redo</Button>
            <Button ghost icon onClick={remove} aria-label="Remove announcement">✕</Button>
          </>
        ) : (
          <Button primary block onClick={start} aria-label="Record announcement" disabled={!Recorder.supported() && !MOCK()}>● Record name</Button>
        )}
      </div>
      {!Recorder.supported() && !MOCK() && <div className="hint" style={{ color: "var(--danger)" }}>This browser can't record audio. On iPhone, use Safari.</div>}
    </motion.section>
  );
}
